import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { RefreshCw, Link2, Trash2, Plus, Loader2 } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

import { API_URL } from "@/lib/api";
const API = `${API_URL}/api`;

const DATA_TYPE_LABELS = {
  inventario: "Inventario",
  ingresos: "Ingresos QMS",
  ventas: "Ventas MS",
};

const LocalSheetsConfig = ({ localId, localName, onSyncComplete }) => {
  const [configs, setConfigs] = useState([]);
  const [showDialog, setShowDialog] = useState(false);
  const [syncing, setSyncing] = useState(null);
  const [newConfig, setNewConfig] = useState({ name: "", url: "", data_type: "inventario", sheet_name: "" });
  const [saving, setSaving] = useState(false);

  const fetchConfigs = async () => {
    try {
      const res = await axios.get(`${API}/local/${localId}/sheets/config`);
      setConfigs(res.data.configs);
    } catch { /* no configs yet */ }
  };

  useEffect(() => { if (showDialog) fetchConfigs(); }, [showDialog, localId]);

  const handleSave = async () => {
    if (!newConfig.name || !newConfig.url) { toast.error("Completa nombre y URL"); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/local/${localId}/sheets/config`, newConfig);
      toast.success("Configuracion guardada");
      setNewConfig({ name: "", url: "", data_type: "inventario", sheet_name: "" });
      fetchConfigs();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al guardar");
    } finally { setSaving(false); }
  };

  const handleSync = async (dataType) => {
    setSyncing(dataType);
    try {
      const res = await axios.post(`${API}/local/${localId}/sync/${dataType}`);
      toast.success(res.data.message);
      if (onSyncComplete) onSyncComplete();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al sincronizar");
    } finally { setSyncing(null); }
  };

  const handleDelete = async (dataType) => {
    try {
      await axios.delete(`${API}/local/${localId}/sheets/config/${dataType}`);
      toast.success("Configuracion eliminada");
      fetchConfigs();
    } catch { toast.error("Error al eliminar"); }
  };

  return (
    <>
      <Button variant="outline" size="sm" onClick={() => setShowDialog(true)}
        className="border-emerald-500 text-emerald-700 hover:bg-emerald-50 text-xs h-7 gap-1.5"
        data-testid="local-sheets-config-btn">
        <Link2 className="w-3 h-3" />Google Sheets
      </Button>

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-lg font-semibold text-slate-800">
              {localName} - Google Sheets
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {configs.length > 0 && (
              <div className="space-y-2">
                <Label className="text-sm font-medium text-slate-700">Hojas configuradas</Label>
                {configs.map(c => (
                  <div key={c.data_type} className="flex items-center gap-2 p-3 bg-slate-50 rounded-lg border"
                    data-testid={`local-sheet-${c.data_type}`}>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-slate-800 truncate">{c.name}</p>
                      <p className="text-xs text-slate-500 truncate">
                        {DATA_TYPE_LABELS[c.data_type] || c.data_type}
                        {c.sheet_name ? ` (${c.sheet_name})` : ""}
                      </p>
                    </div>
                    <Button size="sm" onClick={() => handleSync(c.data_type)} disabled={syncing !== null}
                      className="bg-emerald-600 hover:bg-emerald-700 text-white shrink-0"
                      data-testid={`local-sync-${c.data_type}-btn`}>
                      {syncing === c.data_type ? (
                        <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />Sincronizando...</>
                      ) : (
                        <><RefreshCw className="w-3.5 h-3.5 mr-1.5" />Sincronizar</>
                      )}
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => handleDelete(c.data_type)}
                      className="text-rose-500 hover:bg-rose-50 shrink-0 px-2">
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                ))}
              </div>
            )}

            <div className="border-t pt-4 space-y-3">
              <Label className="text-sm font-medium text-slate-700">Agregar / Actualizar hoja</Label>
              <Input placeholder="Nombre (ej. INVENTARIO MS)" value={newConfig.name}
                onChange={e => setNewConfig(p => ({ ...p, name: e.target.value }))}
                data-testid="local-sheet-name-input" />
              <Input placeholder="URL de Google Sheets" value={newConfig.url}
                onChange={e => setNewConfig(p => ({ ...p, url: e.target.value }))}
                data-testid="local-sheet-url-input" />
              <Select value={newConfig.data_type} onValueChange={v => setNewConfig(p => ({ ...p, data_type: v }))}>
                <SelectTrigger data-testid="local-sheet-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="inventario">Inventario</SelectItem>
                  <SelectItem value="ingresos">Ingresos QMS</SelectItem>
                  <SelectItem value="ventas">Ventas MS</SelectItem>
                </SelectContent>
              </Select>
              <Input placeholder="Nombre de pestana (ej. INVENTARIO MS) - opcional" value={newConfig.sheet_name}
                onChange={e => setNewConfig(p => ({ ...p, sheet_name: e.target.value }))}
                data-testid="local-sheet-tab-input" />
              <Button onClick={handleSave} disabled={saving || !newConfig.name || !newConfig.url}
                className="w-full bg-sky-600 hover:bg-sky-700 text-white"
                data-testid="local-save-sheet-btn">
                {saving ? "Guardando..." : <><Plus className="w-4 h-4 mr-2" />Guardar configuracion</>}
              </Button>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
              <p className="text-xs text-amber-800">
                <strong>Requisito:</strong> La hoja debe estar compartida como <strong>&quot;Cualquier persona con el enlace - Lector&quot;</strong> para que el sistema pueda leerla.
              </p>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default LocalSheetsConfig;
